import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Spinner, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Users, Package, Printer, Search, Layers, Truck, Trash2, MessageCircle, GitMerge } from "lucide-react";

const CAT_LABELS = { cx1: "CX1", cx2: "CX2", cx3: "CX3", cx3g: "CX3G", cx4: "CX4" };

export default function TerceirosPallets() {
  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [clienteFilter, setClienteFilter] = useState("");
  const [incluirRetirados, setIncluirRetirados] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeOrigem, setMergeOrigem] = useState("");
  const [mergeDestino, setMergeDestino] = useState("");
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeErr, setMergeErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = {};
      if (q) params.q = q;
      if (clienteFilter) params.cliente_dono = clienteFilter;
      if (incluirRetirados) params.incluir_retirados = true;
      const { data } = await api.get("/terceiros/pallets", { params });
      setItems(data.items || []);
      setResumo(data.resumo || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clienteFilter, incluirRetirados]);

  const onDelete = async (pid, numero, status) => {
    if (status === "expedido") {
      alert(`Pallet ${numero} já foi retirado. Cancele a retirada correspondente antes de excluir.`);
      return;
    }
    if (!window.confirm(`Excluir o pallet ${numero}? Esta ação não pode ser desfeita. A sacaria consumida (se houver) volta automaticamente ao estoque.`)) return;
    try {
      await api.delete(`/pallets/${pid}`);
      await load();
    } catch (e) { alert(formatErr(e)); }
  };

  const openMerge = (origem = "") => {
    setMergeOrigem(origem);
    setMergeDestino("");
    setMergeErr("");
    setMergeOpen(true);
  };

  const doMerge = async () => {
    const origem = (mergeOrigem || "").trim();
    const destino = (mergeDestino || "").trim();
    if (!origem || !destino) { setMergeErr("Selecione origem e destino."); return; }
    if (origem === destino) { setMergeErr("Origem e destino são iguais."); return; }
    if (!window.confirm(`Mesclar TODAS as NFs, pallets e retiradas de "${origem}" para "${destino}"?\n\nEsta ação renomeia o cliente em todo o histórico e não pode ser desfeita automaticamente.`)) return;
    setMergeBusy(true); setMergeErr("");
    try {
      const { data } = await api.post("/terceiros/merge-clientes", { origem, destino });
      alert(`✅ Mesclado com sucesso!\n\nInvoices atualizadas: ${data.invoices}\nPallets: ${data.pallets}\nRetiradas: ${data.retiradas}`);
      setMergeOpen(false);
      setClienteFilter("");
      await load();
    } catch (e) { setMergeErr(formatErr(e)); }
    finally { setMergeBusy(false); }
  };

  const totais = items.reduce(
    (a, p) => {
      const restante = Number(p.restante ?? (Number(p.total_caixas || 0) - Number(p.quantidade_expedida || 0)));
      if (restante <= 0 && !incluirRetirados) return a;
      return {
        pallets: a.pallets + 1,
        sacos: a.sacos + (incluirRetirados ? Number(p.total_caixas || 0) : restante),
        valor: a.valor + (incluirRetirados ? Number(p.total_caixas || 0) : restante) * Number(p.preco_servico_sc || 0),
      };
    },
    { pallets: 0, sacos: 0, valor: 0 }
  );

  const shareWhatsApp = () => {
    const now = new Date();
    const dataStr = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

    const blocosCliente = (resumo || [])
      .sort((a, b) => (b.sacos || 0) - (a.sacos || 0))
      .map((r) => {
        const linhasCat = Object.entries(r.por_categoria || {})
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `*${CAT_LABELS[k]}*     ${fmtNum(v, 0)}`)
          .join("\n");
        return (
`*${r.cliente_dono}*
${r.pallets} pallets  •  ${fmtNum(r.sacos, 0)} sacos

${linhasCat}`
        );
      })
      .join("\n\n━━━━━━━━━━━━━━━━━━\n\n");

    const corpo = resumo.length === 0
      ? "_Nenhum pallet de terceiros no momento._"
      : blocosCliente;

    const totalGeral = resumo.reduce((s, r) => s + (r.sacos || 0), 0);
    const totalPallets = resumo.reduce((s, r) => s + (r.pallets || 0), 0);

    const msg =
`*ESTOQUE DE TERCEIROS*
_Cebolas Serenata • ${dataStr}_

━━━━━━━━━━━━━━━━━━

${corpo}${resumo.length > 1 ? `\n\n━━━━━━━━━━━━━━━━━━\n*TOTAL GERAL*\n${totalPallets} pallets  •  ${fmtNum(totalGeral, 0)} sacos` : ""}

━━━━━━━━━━━━━━━━━━
🌱 _Gestão feita com *Dataneres* — o ERP das cebolas_
_dataneres.com_`;

    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Layout
      title={incluirRetirados ? "Pallets de Terceiros — Histórico Completo" : "Estoque de Terceiros"}
      subtitle={incluirRetirados ? "Todos os pallets já processados (inclui retirados)" : "Pallets em estoque atualmente no pátio, isolados por cliente"}
      actions={
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer select-none" data-testid="incluir-retirados-label">
            <input
              type="checkbox"
              checked={incluirRetirados}
              onChange={(e) => setIncluirRetirados(e.target.checked)}
              className="w-4 h-4 accent-purple-600"
              data-testid="incluir-retirados-toggle"
            />
            Incluir retirados (histórico)
          </label>
          <Button
            onClick={shareWhatsApp}
            className="!bg-emerald-600 hover:!bg-emerald-700"
            data-testid="share-whatsapp-stock"
            title="Envia resumo do estoque via WhatsApp"
          >
            <MessageCircle size={16} /> WhatsApp
          </Button>
          <Link to="/terceiros/retiradas/new">
            <Button className="!bg-purple-600 hover:!bg-purple-700" data-testid="new-retirada-from-pallets">
              <Truck size={16} /> Nova Retirada
            </Button>
          </Link>
        </div>
      }
    >
      {/* Totais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">{incluirRetirados ? "Pallets (histórico)" : "Pallets em estoque"}</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{totais.pallets}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Clientes distintos</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{resumo.length}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Total sacos</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{fmtNum(totais.sacos, 0)}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">A faturar (serviço)</div>
          <div className="font-mono text-2xl font-bold text-purple-800 mt-1">{fmtBRL(totais.valor)}</div>
        </Card>
      </div>

      {/* Resumo por cliente */}
      {resumo.length > 0 && (
        <Card className="p-5 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500">Resumo por Cliente</div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openMerge()}
              title="Mesclar variações de nomes de clientes (ex: 'dimas' vs 'DIMAS')"
              data-testid="btn-mesclar-clientes"
            >
              <GitMerge size={14} /> Mesclar clientes
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {resumo.map((r) => (
              <div
                key={r.cliente_dono}
                className={`p-5 rounded-lg border cursor-pointer transition-colors ${clienteFilter === r.cliente_dono ? "bg-purple-100 border-purple-500" : "bg-purple-50/40 border-purple-200 hover:bg-purple-50"}`}
                onClick={() => setClienteFilter(clienteFilter === r.cliente_dono ? "" : r.cliente_dono)}
                data-testid={`resumo-cliente-${r.cliente_dono}`}
              >
                {/* Nome do cliente */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Users size={16} className="text-purple-700 shrink-0" />
                    <div className="font-bold text-purple-900 truncate text-lg">{r.cliente_dono}</div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openMerge(r.cliente_dono); }}
                    className="p-1 rounded hover:bg-purple-200 text-purple-600 shrink-0"
                    title="Mesclar este cliente em outro"
                    data-testid={`btn-mesclar-${r.cliente_dono}`}
                  >
                    <GitMerge size={13} />
                  </button>
                </div>

                {/* Destaque: total de sacos */}
                <div className="text-center py-2 mb-3 bg-white rounded-md border border-purple-200">
                  <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-1">Total em Estoque</div>
                  <div className="text-4xl font-black text-purple-900 leading-none tabular-nums" style={{fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"}}>
                    {fmtNum(r.sacos, 0)}
                  </div>
                  <div className="text-xs text-ink-500 mt-1">sacos • {r.pallets} pallets</div>
                </div>

                {/* Breakdown por categoria — tabela linha a linha */}
                <div className="space-y-1.5">
                  {Object.entries(r.por_categoria).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between items-center px-3 py-1.5 rounded bg-white border border-purple-100">
                      <span className="text-sm font-bold text-purple-700">{CAT_LABELS[k]}</span>
                      <span className="text-xl font-bold text-ink-900 tabular-nums" style={{fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"}}>
                        {fmtNum(v, 0)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Rodapé: valor a faturar */}
                <div className="mt-3 pt-3 border-t border-purple-200 flex justify-between items-baseline">
                  <span className="text-xs text-ink-600 uppercase tracking-wider font-semibold">A faturar</span>
                  <span className="text-lg font-black text-purple-800 tabular-nums" style={{fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"}}>{fmtBRL(r.valor_servico)}</span>
                </div>
              </div>
            ))}
          </div>
          {clienteFilter && (
            <div className="mt-3 text-xs text-purple-800 flex items-center gap-2">
              Filtrando por: <strong>{clienteFilter}</strong>
              <button onClick={() => setClienteFilter("")} className="underline text-purple-600" data-testid="clear-filter-btn">limpar</button>
            </div>
          )}
        </Card>
      )}

      {/* Busca */}
      <Card className="p-4 mb-4">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Pallet, NF ou cliente..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="pallets-search"
              />
            </div>
          </div>
          <Button onClick={load} className="!bg-purple-600 hover:!bg-purple-700" data-testid="pallets-buscar">
            <Search size={14} /> Buscar
          </Button>
        </div>
      </Card>

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-3">{err}</div>}

      {/* Lista de pallets */}
      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner size={20} /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-ink-500">
            <Layers size={32} className="mx-auto mb-2 text-ink-300" />
            <div>Nenhum pallet de terceiros encontrado.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3 text-left">Pallet</th>
                  <th className="px-4 py-3 text-left">Cliente (dono)</th>
                  <th className="px-4 py-3 text-left">NF</th>
                  <th className="px-4 py-3 text-left">Categoria</th>
                  <th className="px-4 py-3 text-right">{incluirRetirados ? "Sacos" : "Saldo"}</th>
                  <th className="px-4 py-3 text-right">R$/sc</th>
                  <th className="px-4 py-3 text-right">A faturar</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const it = (p.itens || [])[0] || {};
                  const qtdOrig = Number(it.quantidade || 0);
                  const restante = Number(p.restante ?? (Number(p.total_caixas || 0) - Number(p.quantidade_expedida || 0)));
                  const parcial = restante > 0 && restante < qtdOrig;
                  const preco = Number(p.preco_servico_sc || 0);
                  return (
                    <tr key={p.id} className={`border-b border-ink-200 hover:bg-purple-50/30 ${parcial ? "bg-amber-50/50" : ""}`}>
                      <td className="px-4 py-3 font-mono font-bold">{p.numero}</td>
                      <td className="px-4 py-3 font-semibold text-purple-900">{p._inv_cliente_dono || p.cliente_dono || "—"}</td>
                      <td className="px-4 py-3 font-mono">#{p._inv_nota_numero || p.nota_numero || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge tone="neutral" className="!bg-purple-100 !text-purple-700 !border-purple-200">{CAT_LABELS[it.categoria] || it.categoria}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        <div className={parcial ? "text-amber-800 font-bold" : ""}>{fmtNum(restante, 0)}</div>
                        {parcial && <div className="text-[10px] text-amber-600 leading-tight">de {fmtNum(qtdOrig, 0)} orig.</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-ink-500">R$ {preco.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-purple-800">{fmtBRL(restante * preco)}</td>
                      <td className="px-4 py-3">
                        <Badge tone={p.status === "expedido" ? "neutral" : parcial ? "warning" : "success"}>{parcial ? "parcial" : p.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`/pallet-label/${p.id}`} target="_blank" className="text-purple-600 text-xs font-semibold hover:underline inline-flex items-center gap-1" data-testid={`pallet-print-${p.id}`}>
                            <Printer size={12} /> Etiqueta
                          </Link>
                          <button
                            onClick={() => onDelete(p.id, p.numero, p.status)}
                            className="p-1.5 rounded-md hover:bg-red-50 text-danger disabled:opacity-40 disabled:hover:bg-transparent"
                            title={p.status === "expedido" ? "Pallet já retirado — cancele a retirada antes" : "Excluir pallet"}
                            disabled={p.status === "expedido"}
                            data-testid={`pallet-delete-${p.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal — Mesclar clientes */}
      {mergeOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !mergeBusy && setMergeOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <GitMerge size={20} className="text-purple-700" />
              <h3 className="font-heading text-xl font-bold text-ink-900">Mesclar Clientes</h3>
            </div>
            <p className="text-sm text-ink-600 mb-4">
              Selecione a variação a ser <strong>renomeada</strong> (origem) e o nome canônico (destino). Todas as NFs, pallets e retiradas serão atualizadas.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">De (origem)</label>
                <select
                  value={mergeOrigem}
                  onChange={(e) => setMergeOrigem(e.target.value)}
                  disabled={mergeBusy}
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="merge-origem"
                >
                  <option value="">Selecione...</option>
                  {resumo.map((r) => <option key={r.cliente_dono} value={r.cliente_dono}>{r.cliente_dono}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Para (destino canônico)</label>
                <input
                  type="text"
                  value={mergeDestino}
                  onChange={(e) => setMergeDestino(e.target.value)}
                  disabled={mergeBusy}
                  placeholder="Digite o nome final (ex: Dimas)"
                  list="clientes-terceiros"
                  className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="merge-destino"
                />
                <datalist id="clientes-terceiros">
                  {resumo.map((r) => <option key={r.cliente_dono} value={r.cliente_dono} />)}
                </datalist>
                <p className="text-[11px] text-ink-500 mt-1">Pode ser um cliente existente (mescla os estoques) ou um nome novo (só renomeia).</p>
              </div>
              {mergeErr && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{mergeErr}</div>}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="secondary" onClick={() => setMergeOpen(false)} disabled={mergeBusy}>Cancelar</Button>
              <Button onClick={doMerge} disabled={mergeBusy || !mergeOrigem || !mergeDestino} data-testid="merge-confirm">
                {mergeBusy ? <Spinner size={14} /> : <><GitMerge size={14} /> Mesclar</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
